'use client';

import type { TooltipWalkthroughStep } from '@unifyapps/defs/types/page/walkthrough';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { useCallback, useMemo } from 'react';
import { useWalkthroughManagerStore } from '../../../../stores/WalkthroughManagerStore';
import { WalkthroughMode } from '../../../../stores/WalkthroughManagerStore/const';
import { useTooltipStepTransition } from '../../../hooks/useTooltipStepTransition';
import { useWalkthroughOrchestrator } from '../../../hooks/useWalkthroughOrchestrator';
import { useHighlightOverlay } from '../hooks/useHighlightOverlay';
import { useWalkthroughHighlightSync } from '../hooks/useWalkthroughHighlightSync';
import { useWalkthroughResizeHandlers } from '../hooks/useWalkthroughResizeHandlers';
import { useWalkthroughStepClickHandlers } from '../hooks/useWalkthroughStepClickHandlers';
import { useWalkthroughTargetElement } from '../hooks/useWalkthroughTargetElement';
import { useWalkthroughTargetPointerEvents } from '../hooks/useWalkthroughTargetPointerEvents';
import { resolveWalkthroughInteractionSettings } from '../utils/resolveWalkthroughInteractionSettings';
import { HighlightOverlay } from './HighlightOverlay';

export interface WalkthroughTooltipStepCoreProps {
  step: TooltipWalkthroughStep;
  children: (props: {
    targetElement: HTMLElement | null;
    animationState: { isAnimating: boolean; animationProgress: number };
    containerEl: HTMLElement | null;
    handleResize: () => void;
  }) => React.ReactNode;
  handleMissingTargetElement?: () => void;
}

/**
 * WalkthroughTooltipStepCore - Handles all tooltip-specific logic:
 * - Target element finding
 * - Highlight overlay
 * - Resize handlers
 * - Click handlers
 *
 * Only used for tooltip display type steps in RUNNER mode.
 * All step settings are respected in runner mode, including:
 * - elementMissingBehavior (skip/end walkthrough)
 * - waitUntilElementAppears / waitUntilElementAppearsDelay
 * - goToNextStepOnClickOf (click handlers)
 * - showAfter / showAfterDelay / showAfterScroll
 */
export function WalkthroughTooltipStepCore({
  step,
  children,
  handleMissingTargetElement,
}: WalkthroughTooltipStepCoreProps) {
  const containerEl = useContainerElementContext();
  const { moveStep } = useWalkthroughOrchestrator();
  const mode = useWalkthroughManagerStore().hooks.useMode();
  const transitionState = useWalkthroughManagerStore().hooks.useTooltipTransitionState();
  // In BUILDER or PREVIEW mode, don't apply skip/end behavior for missing elements
  const isBuilderOrPreview = mode === WalkthroughMode.BUILDER || mode === WalkthroughMode.PREVIEW;
  const isPreparing = transitionState?.status === 'preparing';

  // Initialize transition state machine for tooltip steps
  // This hook manages the transition state when moving between steps
  useTooltipStepTransition(step);
  const {
    focusShape,
    showBackdrop = true,
    overlayColor,
    overlayOpacity,
    goToNextStepOnClickOf,
    elementMissingBehavior,
    waitUntilElementAppears,
    waitUntilElementAppearsDelay,
    targetBlockId,
    targetType,
    targetElementQuery,
    customCSSSelector,
  } = step;

  // Resolve effective interaction settings based on goToNextStepOnClickOf
  const { enableInteractionWithPage, enableInteractionWithTargetElement } = useMemo(
    () => resolveWalkthroughInteractionSettings(step),
    [step],
  );

  // Use our custom hook to handle the SVG overlay that highlights elements
  const { svgRef, pathRef, pathElement, highlightElement, animationState, handleResize, stage } =
    useHighlightOverlay(containerEl ?? null, focusShape);

  // Find target element
  // When waitUntilElementAppears is true, targetElement will be null until the element appears
  // (and delay is applied if waitUntilElementAppearsDelay is specified)
  const { targetElement } = useWalkthroughTargetElement(
    {
      id: step.id,
      targetBlockId,
      targetType,
      targetElementQuery,
      customCSSSelector,
      waitUntilElementAppears,
      waitUntilElementAppearsDelay,
    },
    containerEl,
    elementMissingBehavior,
    isBuilderOrPreview,
    handleMissingTargetElement,
  );

  useWalkthroughTargetPointerEvents(
    targetElement,
    enableInteractionWithTargetElement,
    goToNextStepOnClickOf,
  );

  // Unified update handler for all resize/scroll events
  const handleUpdate = useCallback(() => {
    if (!containerEl || !targetElement) {
      return;
    }

    // Always update highlight (element position may have changed)
    handleResize();
    highlightElement(targetElement);
  }, [containerEl, targetElement, handleResize, highlightElement]);

  // Expose handleResize for children to call when tooltip updates
  // Use useCallback to prevent infinite loops - this function is passed as onUpdate prop
  const exposedHandleResize = useCallback(() => {
    if (targetElement) {
      handleResize();
      highlightElement(targetElement);
    }
  }, [targetElement, handleResize, highlightElement]);

  // Use extracted hook for resize/scroll handling
  useWalkthroughResizeHandlers({
    targetBlockId,
    containerEl,
    onUpdate: handleUpdate,
    isBuilderOrPreview: false,
  });

  useWalkthroughHighlightSync(targetElement, highlightElement);

  useWalkthroughStepClickHandlers(
    targetElement,
    pathElement,
    goToNextStepOnClickOf,
    showBackdrop,
    moveStep,
  );

  if (isPreparing) {
    return null;
  }

  return (
    <>
      {/*
        The SVG overlay - this creates a semi-transparent dark layer over the whole page
        with a "hole" cut out where our highlighted element is.
        Only shown if showBackdrop is true.
      */}
      {showBackdrop && (
        <HighlightOverlay
          enableInteractionWithPage={enableInteractionWithPage}
          enableInteractionWithTargetElement={enableInteractionWithTargetElement}
          overlayColor={overlayColor}
          overlayOpacity={overlayOpacity}
          pathRef={pathRef}
          svgRef={svgRef}
          targetBounds={stage}
        />
      )}
      {children({
        targetElement,
        animationState,
        containerEl,
        handleResize: exposedHandleResize,
      })}
    </>
  );
}
